import React, { useState, useEffect } from "react"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import { showMessageDialog } from "aldoo-ra/MessageDialog"
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline"

/**
 * License Export Complete Dialog Component
 * Shows export results using GridSummary with countdown timer
 */
export const LicenseExportCompleteDialog = {
  /**
   * Show export completion dialog with countdown and download
   * @param {Object} exportResult - Result from ExportLicenses API
   * @param {number} exportResult.totalExported - Number of licenses exported
   * @param {string} exportResult.filename - Generated filename
   * @param {string} exportResult.downloadLink - Download URL
   * @param {number} [exportResult.expiresIn=5] - Expiration time in minutes
   */
  show: (exportResult) => {
    const expiresInMinutes = exportResult.expiresIn || 5
    let timeLeftSeconds = expiresInMinutes * 60 // Convert to seconds

    // Create a functional component for the dialog content
    const DialogContent = () => {
      const [countdown, setCountdown] = useState(timeLeftSeconds)
      const [isExpired, setIsExpired] = useState(false)

      useEffect(() => {
        const interval = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              setIsExpired(true)
              clearInterval(interval)
              return 0
            }
            return prev - 1
          })
        }, 1000)

        return () => clearInterval(interval)
      }, [])

      const formatTime = (seconds) => {
        if (isExpired) return "Expired"
        const minutes = Math.floor(seconds / 60)
        const secs = seconds % 60
        return `${minutes}:${secs.toString().padStart(2, '0')}`
      }

      return (
        <div className="space-y-4">
          <div className="text-center mb-4">
            <span className="text-admin_success text-lg">✅</span>
            <span className="ml-2 text-admin_text font-medium">Export completed successfully!</span>
          </div>
          
          {/* Vertical layout with 2 full-width cells */}
          <div className="space-y-4 w-full">
            {/* Top: Licenses Exported - Full width */}
            <div className="bg-admin_paper border border-admin_bordered rounded-lg p-6 w-full">
              <Typography variant="admin_txt2" className="uppercase tracking-wider mb-3 text-admin_text_secondary text-xs">
                Licenses Exported
              </Typography>
              <Typography variant="admin_body" className="font-semibold text-admin_text text-lg">
                {exportResult.totalExported} license{exportResult.totalExported === 1 ? '' : 's'}
              </Typography>
            </div>
            
            {/* Bottom: Download Link Expires with button - Full width */}
            <div className="bg-admin_paper border border-admin_bordered rounded-lg p-6 w-full">
              <Typography variant="admin_txt2" className="uppercase tracking-wider mb-3 text-admin_text_secondary text-xs">
                Download Link Expires in
              </Typography>
              <div className="flex items-center justify-between">
                <Typography 
                  variant="admin_body" 
                  className={`font-semibold text-lg ${isExpired ? "text-admin_error" : "text-admin_text"}`}
                >
                  {formatTime(countdown)}
                </Typography>
                <Button
                  variant="admin_primary"
                  onClick={() => {
                    if (!isExpired) {
                      window.open(exportResult.downloadLink, '_blank')
                    }
                  }}
                  disabled={isExpired}
                  className="flex items-center ml-4"
                >
                  <ArrowDownTrayIcon className="w-5 h-5 mr-2" />
                  {isExpired ? "Expired" : "Download"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )
    }

    showMessageDialog({
      title: "Export Complete",
      message: <DialogContent />,
      btnLabel: "", // Hide the bottom button
      showCloseX: true, // Show X close button
    })
  },
}

export default LicenseExportCompleteDialog